import {Component, HostListener, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {SectionsContainer} from '../../core/definitions/sections-container';
import {ObjectEditService} from '../../core/object-edit.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Reference} from '../../core/definitions/reference';
import {UiToolsService} from '../../core/ui-tools.service';
import {ReferenceUsageInfo, SearchReferenceService} from "../../core/search-reference.service";
import {EditObjectDialogService} from "../edit-object-dialog.service";
import {EditObjectDialogData} from "../edit-object-dialog-data";
import {TranslateService} from "@ngx-translate/core";
import {CmsApiService} from "../../core/cms-api.service";
import {SuperObjectModel} from "../../core/definitions/super-object-model";
import {SearchObject} from "../../core/definitions/search-object";
import {SearchSelectorService} from "../../core/search-selector.service";
import {SelectorContainer} from "../../core/definitions/selector-container";
import {
  EditObjectDialogSelectorWrapper
} from "../edit-object-dialog-selector-wrapper/edit-object-dialog-selector-wrapper";
import {AlertDialogComponent} from "../../shared/alert-dialog/alert-dialog.component";
import {ConfirmDialogData} from "../confirm-dialog/confirm-dialog.component";

export interface SelectObjectType {
  id: string;
  name: string;
  objectType: string;
}

enum EditDialogStep {
  SelectObjectType = 'SelectObjectType',
  SelectDataSource = 'SelectDataSource',
  EditObject = 'EditObject',
  CreateFromExisting = 'CreateFromExisting',
}

@Component({
  selector: 'app-edit-object-dialog',
  templateUrl: './edit-object-dialog.component.html',
  styleUrls: ['./edit-object-dialog.component.scss'],
  standalone: false
})
export class EditObjectDialogComponent implements OnInit {
  // Injections
  public dialogRef: MatDialogRef<EditObjectDialogComponent> = inject(MatDialogRef);
  public data: EditObjectDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly editObjectDialogService: EditObjectDialogService = inject(EditObjectDialogService);
  private readonly objectEditService: ObjectEditService = inject(ObjectEditService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);
  private readonly cms: CmsApiService = inject(CmsApiService);

  public EditDialogStep = EditDialogStep;
  public currentStep: EditDialogStep;

  displayCopyOptions: boolean;
  sectionsContainer: SectionsContainer = new SectionsContainer();
  private fieldReference: Reference;
  primeFields: any[];
  storeStatus = {
    failed: false,
    message: '',
    serverMsg: ''
  };
  selectObjectTypes: SelectObjectType[];
  selectedObjectTypeName = null;
  selectedObjectType: string;
  selectedTargetCount: number = 1;
  modelTitle = '';
  referenceUsageInfo: ReferenceUsageInfo;

  selectorContainer: WritableSignal<SelectorContainer | null> = signal(null);
  selectedAction: WritableSignal<string> = signal('new_object');
  selectedSource: WritableSignal<SearchObject> = signal(null);

  @HostListener('click', ['$event.target'])
  registerClick(event: any) {
    this.uiTools.registerDocumentClick(event);
  }

  ngOnInit() {
    this.initObject().then(() => {
      this.currentStep = this.selectObjectTypes ? EditDialogStep.SelectObjectType : EditDialogStep.EditObject;
    });
  }

  onSubmit() {
    if (this.referenceUsageInfo.usage.length && this.referenceUsageInfo.usageConfirmStoreText) {
      const dialogText = this.translate.instant(this.referenceUsageInfo.usageConfirmStoreText);
      if (!window.confirm(dialogText)) {
        return;
      }
    }
    this.objectEditService.setObjectValuesStoreObject(this.sectionsContainer, true).then(
      value => {
        this.dialogRef.close(value);

        if (this.selectedTargetCount > 1 && (!Array.isArray(value) || value.length < this.selectedTargetCount)) {
          this.dialog.open(AlertDialogComponent, {
            panelClass: 'alert-dialog',
            data: {
              modalTitle: 'TRANS__EDIT_OBJECT_DIALOG__STORE_WARNING',
              modalContent: this.translate.instant(
                'TRANS__EDIT_OBJECT_DIALOG__STORE_FAILED_INCOMPLETE', {
                  count: Array.isArray(value) ? value.length : !!value ? 1 : 0,
                  target: this.selectedTargetCount,
                }),
              modalButton: 'TRANS__ALERT_DIALOG__OK',
            } as ConfirmDialogData
          });
        }
      },
      reason => {
        this.storeStatus.failed = true;
        this.storeStatus.message = 'TRANS__EDIT_OBJECT_DIALOG__STORE_FAILED';
        this.storeStatus.serverMsg = reason?.errorMessage || reason.error?.message;
      }
    );
  }

  get isValid(): boolean {
    return this.sectionsContainer.formGroup.valid;
  }

  get isNextStepEnabled(): boolean {
    return this.selectedObjectTypeName !== null && this.selectedTargetCount > 0
  }

  actionSelected(action: string) {
    this.selectedAction.set(action);
  }

  objectTypeSelected(selectObjectType: SelectObjectType) {
    if (selectObjectType.objectType) {
      this.selectedObjectTypeName = selectObjectType.objectType;
      this.selectedObjectType = selectObjectType.objectType;
    }
  }

  targetCountSelected(count: number) {
    this.selectedTargetCount = count;
  }

  nextStep() {
    const selectedAction = this.selectedAction();
    if (selectedAction === 'copy_from_other') {
      // How to enable selector?
      this.selectorContainer.set(this.searchSelectorService.createSelectorContainer(this.data.fieldParameters.sectionsContainer.rootObject));

      let dialog = this.dialog.open(EditObjectDialogSelectorWrapper, {
        data: {
          selector: this.fieldReference.selector,
          selectorContainer: this.selectorContainer()
        }
      });

      dialog.afterClosed().subscribe(result => {
        if (result?.length) {
          this.selectedSource.set(result[0]);
          this.createCopySectionsContainer(result[0].artifact_id, result[0].object_type).then(sectionsContainer => {
            this.sectionsContainer = sectionsContainer;
            this.currentStep = EditDialogStep.EditObject;
          });
        }
      })

    } else if (selectedAction === 'copy_from_parent') {
      this.createCopySectionsContainer(this.data.fieldParameters.sectionsContainer.rootObject.artifact_id,
        this.data.fieldParameters.sectionsContainer.rootObject.object_type).then(sectionsContainer => {
        this.sectionsContainer = sectionsContainer;
        this.currentStep = EditDialogStep.EditObject;
      });
    } else {
      if (this.currentStep === EditDialogStep.SelectObjectType) {
        const createMultiple = !!(this.selectedTargetCount && this.selectedTargetCount > 1);
        const {
          use_context_creating_identifier: useContextCreatingIdentifier = false
        } = this.fieldReference.add_new_params || {};
        this.data.fieldParameters.sectionsContainer.useContextCreatingIdentifier = useContextCreatingIdentifier;
        this.editObjectDialogService.createObjectSectionsContainer(
          this.selectedObjectType,
          this.data,
          this.fieldReference,
          this.data.fieldParameters.object,
          undefined,
          undefined,
          createMultiple,
        ).then(sectionsContainer => {
          this.sectionsContainer = sectionsContainer;
          if (createMultiple) {
            this.sectionsContainer.rootObject.create_multiple_count = this.selectedTargetCount;
          }
          if (sectionsContainer.useContextCreatingIdentifier && sectionsContainer.operationContextObjects) {
            // @ts-ignore
            this.sectionsContainer.rootObject.identification_number.context_id =
              sectionsContainer.operationContextObjects[0].artifact_id;
          }
          this.checkSetPrimeFields();
          this.currentStep = EditDialogStep.EditObject;
        });
      }
    }
  }

  get modalTitleText(): string {
    const translationKey = this.modelTitle ? this.modelTitle : 'TRANS__EDIT_OBJECT_DIALOG__CREATE_ENTITY';
    return this.translate.instant(translationKey);
  }

  private async createCopySectionsContainer(artifactId: string, objectType: string) {
    const copyData: SuperObjectModel = await this.cms.copyArtifact({
      artifact_id: artifactId,
      object_type: objectType
    });
    return await this.editObjectDialogService.createObjectSectionsContainer(
      copyData.object_type,
      this.data,
      this.fieldReference,
      copyData,
      null,
      true);
  }

  private async initObject() {
    const fieldParams = this.data.fieldParameters;
    this.fieldReference = this.searchReferenceService.getSearchReferenceFromField(fieldParams.field);
    if (this.fieldReference?.object_type || (fieldParams.object && fieldParams.object.object_type)) {
      this.sectionsContainer = await this.editObjectDialogService.createOptionFromFieldParameters(
        fieldParams, this.data, this.fieldReference, this.data.createText, this.data.parentId);
      if (!this.sectionsContainer) {
        this.storeStatus.failed = true;
        this.storeStatus.message = 'TRANS__EDIT_OBJECT_DIALOG__NO_ACCESS';
      } else {
        this.primeFields = this.sectionsContainer.primeFields;
      }
    } else {
      this.selectObjectTypes = await this.editObjectDialogService.getSelectObjectTypesFromMetaTypes(
        this.fieldReference);
    }

    // @ts-ignore
    this.displayCopyOptions = this.fieldReference.add_new_params?.display_copy_options ?? false;

    this.referenceUsageInfo = await this.searchReferenceService.getReferenceUsage(
      this.data.fieldParameters, this.sectionsContainer?.parentObject)
    this.modelTitle = this.getModelTitle();
  }

  private checkSetPrimeFields() {
    if (this.sectionsContainer && this.editObjectDialogService.getUsePrimeFields(
      this.selectedObjectType, this.fieldReference)) {
      this.primeFields = this.sectionsContainer.primeFields;
    }
  }

  private getModelTitle() {
    let res = '';
    if (this.fieldReference?.selector?.create_button_text) {
      res = this.fieldReference.selector.create_button_text;
    } else if (this.sectionsContainer.rootObject) {
      res = this.sectionsContainer.rootObject.object_model_title || this.sectionsContainer.rootObject.model_title || '';
    }
    return res;
  }

}
